import React, { useState } from "react";

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, Dialog, DialogActions, DialogContent, ListItemIcon, Menu, MenuItem, Portal, SxProps, Theme, Typography, styled } from "@mui/material";

import useDialogState from "@/hooks/useDialogState";
import useKeyboardShortcut from "@/hooks/useKeyboardShortcut";
import { theme } from "@/theme/customTheme";
import { colors } from "@/theme/overrides/colors";
import { typograhpy } from "@/theme/overrides/typography";

import MoreVertIconButton from "./MoreVertIconButton";
import ReplyIconButton from "./ReplyIconButton";

export interface MessageBubbleProps {
  align: "left" | "right";
  sx?: SxProps<Theme>;
  onEdit?: (value: boolean) => void;
  onDelete?: () => void;
  onReply?: () => void;
  isDeleted: true | false;
}

type ButtonProps = {
  borderColor: string;
  textColor: string;
}

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "textColor" && prop !== "textColor"
})<ButtonProps>(({ textColor, borderColor }) => ({
  color: textColor,
  borderColor: borderColor,
  fontWeight: typograhpy.fontWeightBold
}));

const MessageBubble: React.FC<MessageBubbleProps> = ({
  children,
  isDeleted = false,
  ...rootProps
}) => {
  const { align, sx = [], onEdit, onDelete, onReply } = rootProps;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isEditing, setEditing] = useState<boolean>(false);
  const deleteDialog = useDialogState();
  const isReceiver = align === "left";
  const open = Boolean(anchorEl);

  const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
  const handleClosePopover = () => setAnchorEl(null);
  const handleOpenDialog = () => {
    deleteDialog.show();
    handleClosePopover();
  };
  const handleEdit = () => {
    setEditing(true);
    if (onEdit) onEdit(true);
  }

  const handleDelete = () => {
    if (onDelete) onDelete();
    deleteDialog.hide();
  }

  useKeyboardShortcut("Escape", () => {
    setEditing(false);
    if (onEdit) onEdit(false);
  });

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        flexDirection={isReceiver ? "row" : "row-reverse"}
        alignSelf={isReceiver ? "flex-start" : "flex-end"}
      >
        <Box
          borderRadius={theme.spacing(2)}
          bgcolor={colors.white}
          sx={[
            { ...(isReceiver ? { borderTopLeftRadius: 0 } : { borderTopRightRadius: 0 }) },
            ...Array.isArray(sx) ? sx : [sx],
          ]}
        >
          {children}
        </Box>
        {isReceiver &&
          <>
            <Box marginLeft={1} />
            <ReplyIconButton onClick={onReply} />
          </>
        }
        {!isReceiver && !isDeleted && <MoreVertIconButton onClick={handleOpenPopover} />}
        <Menu
          id="basic-menu"
          open={open}
          anchorEl={anchorEl}
          onClick={handleClosePopover}
          onClose={handleClosePopover}
          anchorOrigin={{
            vertical: "top",
            horizontal: isReceiver ? "right" : "left",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: isReceiver ? "left" : "right",
          }}
        >
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" sx={{ color: colors.grey }} />
            </ListItemIcon>
            <Typography
              variant="subtitle1"
              color={colors.grey}
              fontWeight={typograhpy.fontWeightBold}
            >
              Edit
            </Typography>
          </MenuItem>
          <MenuItem
            dense
            sx={{ paddingX: 2 }}
            onClick={handleOpenDialog}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <Typography
              variant="subtitle1"
              color={colors.error}
              fontWeight={typograhpy.fontWeightBold}
            >
              Delete
            </Typography>
          </MenuItem>
        </Menu>
        <Portal>
          <Dialog
            open={deleteDialog.shown}
            onClose={deleteDialog.hide}
            sx={{ '.MuiPaper-root': { padding: 4 } }}
          >
            <DialogContent>
              <Typography variant="subtitle1" fontWeight={typograhpy.fontWeightBold}>
                Are you sure you want to delete this message?
              </Typography>
            </DialogContent>
            <DialogActions sx={{ display: 'flex', justifyContent: 'center' }}>
              <StyledButton
                variant="outlined"
                size="large"
                textColor={colors.error}
                borderColor={colors.error}
                onClick={handleDelete}
              >
                Delete
              </StyledButton>
              <StyledButton
                variant="outlined"
                size="large"
                textColor={colors.primary}
                borderColor={colors.lightGrey}
                onClick={deleteDialog.hide}
              >
                Cancel
              </StyledButton>
            </DialogActions>
          </Dialog>
        </Portal>
      </Box >
      {!isReceiver && isEditing &&
        <Typography variant="caption" sx={{ fontWeight: typograhpy.h4, alignSelf: "flex-end" }}>
          Enter to <span style={{ color: colors.primary }}>Submit</span> •
          Esc to <span style={{ color: colors.error }}>Cancel</span>
        </Typography>
      }
    </>
  );
};

export default MessageBubble;
